/**
 * CHAT-001 — floating chat widget (2026-06-11, Shane).
 *
 * Mounted once in AppShell so the button appears on EVERY authenticated
 * page: floating bottom-right with an unread badge (20s poll). Opens a
 * panel: conversation list → thread (4s poll while open, marked read on
 * view) → or New chat (directory with the same Online dots as Users &
 * Permissions; pick one person for 1:1 — deduped server-side — or
 * several + optional name for a group).
 *
 * Privacy by design: participants only; no delete; the only other read
 * path is the director investigation-export (loudly audited).
 *
 * "Pops up on their screen": short-polling toast + (v1, Shane) the
 * browser Notification API for users who grant permission — fires when
 * unread grows while the panel is closed or the tab is hidden.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAskText } from '../components/useAskText';
import {
  fetchChatDirectory,
  fetchConversations,
  createChatConversation,
  fetchChatMessages,
  sendChatMessage,
  markChatRead,
  investigationExportConversation,
  type ChatConversationPublic,
  type ChatMessagePublic,
} from '../lib/chatApi';
import { fetchMe } from '../lib/meApi';
import { getCurrentUserRole } from '../lib/auth';
import { companyDateIso, formatInZone, useCompanyClock } from '../lib/companyClock';
import { companyTodayIso } from '../lib/companyClock';

type View = 'list' | 'new' | 'thread';

function isOnline(lastSeen: string | null): boolean {
  if (!lastSeen) return false;
  try {
    return Date.now() - new Date(lastSeen).getTime() < 5 * 60 * 1000;
  } catch {
    return false;
  }
}

function timeShort(iso: string, zone: string): string {
  try {
    // "Today" and the time are the company's, not the viewer's browser (TODO 135).
    const d = new Date(iso);
    return companyDateIso(zone, d) === companyDateIso(zone, new Date())
      ? formatInZone(zone, d, { hour: '2-digit', minute: '2-digit' }, 'en-GB')
      : formatInZone(zone, d, { day: 'numeric', month: 'short' }, 'en-GB');
  } catch {
    return '';
  }
}

/** One-to-one chats show the OTHER person's name; groups their title. */
function conversationLabel(c: ChatConversationPublic, myId: string | null): string {
  if (c.isGroup) return c.title ?? c.participants.map((p) => p.name).join(', ');
  const other = c.participants.find((p) => p.userId !== myId);
  return other?.name ?? 'Conversation';
}

export function ChatButton(): JSX.Element {
  const { askText, askTextDialog } = useAskText();
  const { timeZone } = useCompanyClock();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('list');
  const [activeConv, setActiveConv] = useState<ChatConversationPublic | null>(null);

  /*
   * Director investigation export (2026-08-16). Gated in the UI as well as
   * the route — a control nobody else can see is clearer than one that 403s.
   */
  const isDirector = getCurrentUserRole() === 'director';
  const [exporting, setExporting] = useState(false);

  const runInvestigationExport = (): void => {
    if (!activeConv) return;
    // Cancelled — not an error, and nothing is audited because nothing ran.
    askText({
      title: 'Export this conversation',
      label: 'This exports the whole conversation and is recorded against your name. Why is it needed? (at least 10 characters)',
      required: true,
      confirmLabel: 'Export',
      // Above the chat panel, which sits at 1200.
      zIndex: 1300,
      onSubmit: (reason) => void exportWithReason(reason),
    });
  };

  const exportWithReason = async (reason: string): Promise<void> => {
    if (!activeConv) return;
    if (reason.trim().length < 10) {
      setError('A written reason of at least 10 characters is required.');
      return;
    }
    setExporting(true);
    setError(null);
    try {
      const data = await investigationExportConversation(activeConv.id, reason.trim());
      /*
       * Written as a plain transcript rather than JSON — it is read by a
       * person building a case, not by a machine. The reason and the
       * exporting director are on the file itself, so a copy found later
       * still says why it exists.
       */
      const header = [
        `Conversation export — ${conversationLabel(activeConv, myId)}`,
        `Participants: ${data.participants.join(', ')}`,
        `Exported: ${new Date().toISOString()}`,
        `Reason given: ${reason.trim()}`,
        `Messages: ${data.messages.length}`,
        '',
        '---',
        '',
      ].join('\n');
      const body = data.messages
        .map((m) => `[${m.createdAt}] ${m.senderName ?? m.senderUserId}\n${m.body}\n`)
        .join('\n');
      const blob = new Blob([header + body], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation-export-${companyTodayIso()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  };
  const [messages, setMessages] = useState<ChatMessagePublic[]>([]);
  const [draft, setDraft] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);
  const prevUnreadRef = useRef(0);

  const meQ = useQuery({ queryKey: ['auth', 'me'], queryFn: ({ signal }) => fetchMe(signal) });
  const myId = meQ.data?.user.id ?? null;

  // Unread badge — light poll, runs on every page via AppShell.
  const convsQ = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: ({ signal }) => fetchConversations(signal),
    refetchInterval: 20_000,
  });
  const conversations = convsQ.data ?? [];
  const totalUnread = conversations.reduce((n, c) => n + c.unreadCount, 0);

  const directoryQ = useQuery({
    queryKey: ['chat', 'directory'],
    queryFn: ({ signal }) => fetchChatDirectory(signal),
    enabled: open && view === 'new',
    staleTime: 30_000,
  });

  // Browser notification when unread GROWS while the panel is closed
  // (or the tab is hidden) — Shane's "pops up on their screen", v1.
  useEffect(() => {
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = totalUnread;
    if (totalUnread <= prev) return;
    if (open && document.visibilityState === 'visible') return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const top = conversations.find((c) => c.unreadCount > 0);
      try {
        new Notification('JeanieIQ chat', {
          body: top?.lastMessage
            ? `${top.lastMessage.senderName}: ${top.lastMessage.body.slice(0, 80)}`
            : 'New message',
          tag: 'jeanieiq-chat',
        });
      } catch {
        /* notification is best-effort */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalUnread]);

  // Thread poll while open — 4s, full window (≤500), mark read each time.
  useEffect(() => {
    if (!open || view !== 'thread' || !activeConv) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const msgs = await fetchChatMessages(activeConv.id);
        if (cancelled) return;
        setMessages(msgs);
        void markChatRead(activeConv.id).catch(() => {
          /* best-effort */
        });
      } catch {
        /* poll is best-effort */
      }
    };
    void load();
    const id = window.setInterval(() => void load(), 4_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      void qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
    };
  }, [open, view, activeConv, qc]);

  // Keep the newest message in view.
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const sendMut = useMutation({
    mutationFn: (input: { conversationId: string; body: string }) =>
      sendChatMessage(input.conversationId, input.body),
    onSuccess: (m) => {
      setMessages((prev) => [...prev, m]);
      setDraft('');
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not send'),
  });

  const startMut = useMutation({
    mutationFn: () =>
      createChatConversation({
        userIds: Array.from(picked),
        title: groupName.trim() || undefined,
      }),
    onSuccess: async (r) => {
      setPicked(new Set());
      setGroupName('');
      setError(null);
      const fresh = await qc.fetchQuery({
        queryKey: ['chat', 'conversations'],
        queryFn: ({ signal }) => fetchConversations(signal),
      });
      const conv = (fresh ?? []).find((c) => c.id === r.conversationId) ?? null;
      if (conv) {
        setActiveConv(conv);
        setMessages([]);
        setView('thread');
      } else {
        setView('list');
      }
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not start the chat'),
  });

  const openThread = (c: ChatConversationPublic): void => {
    setActiveConv(c);
    setMessages([]);
    setError(null);
    setView('thread');
  };

  const askNotifyPermission = (): void => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  };

  // ─── Styles (platform vars; fixed bottom-right; high z) ────────────────
  /*
   * CHAT-ICON-001 (2026-09-17) — drawn, not typed.
   *
   * Reagan: a better icon for the chat box. It was a speech-balloon emoji,
   * rendered by the operating system, on the one control that floats over
   * every screen in the platform — so it was the least consistent thing in
   * the product and the most seen.
   *
   * Same 16x16 viewBox and 1.5 round stroke as `Icon` in Sidebar.tsx. The
   * size comes from the caller so the launcher and the panel header can
   * differ; the colour comes from currentColor, so neither needs a rule.
   */
  const chatIcon = (size: number): JSX.Element => (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 9.5a2 2 0 01-2 2H6l-3.2 2.6V4a2 2 0 012-2h7.2a2 2 0 012 2z" />
      <path d="M5.8 5.6h4.6M5.8 8h3" />
    </svg>
  );

  /*
   * CHAT-TAB-001 (2026-09-17). Reagan: a side tab on the bottom right that
   * expands when clicked — "the circle box sometimes hides some
   * information".
   *
   * That is the reason, and it is the right one. A 52px circle floating 18px
   * in from the corner sits ON TOP of the page: on any screen whose content
   * reaches the bottom-right it covers a cell or a control, and the only way
   * to read what is under it is to scroll the page around it. Flush to the
   * edge, the tab is in the gutter instead of over the content.
   *
   * right: 0 with no shadow to the right, and rounding on the left corners
   * only, so it reads as attached to the window rather than hovering above
   * it. Vertical text costs no horizontal room, which is what lets it carry
   * the word "Chat" — a nameless glyph in a corner is a guessing game.
   */
  const fab: React.CSSProperties = {
    position: 'fixed',
    right: 0,
    /* Reagan: closer to the bottom. Low enough to be out of the way, clear
     * enough of the edge that it is not mistaken for a scrollbar control. */
    bottom: 24,
    zIndex: 1200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '14px 7px',
    border: 'none',
    borderRadius: '10px 0 0 10px',
    background: 'var(--navy)',
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
    /* Shadow to the LEFT only — nothing casts onto the window edge. */
    boxShadow: '-3px 0 14px rgba(0,0,0,.22)',
  };
  /*
   * Bottom-to-top so it reads upward, which is the convention for a tab on
   * a right edge — the text runs away from the corner rather than into it.
   */
  const fabLabel: React.CSSProperties = {
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: 0.4,
    fontFamily: 'var(--fp, inherit)',
  };
  /*
   * CHAT-TAB-001 — inboard, not on the corner. The badge used to sit at
   * top:-4 right:-4 of the circle, which was fine when the circle floated
   * 18px clear of the edge. On a tab flush at right: 0 that position is off
   * the side of the window.
   */
  const badge: React.CSSProperties = {
    position: 'absolute',
    top: -7,
    left: -9,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    background: 'var(--red)',
    color: 'white',
    fontSize: 11,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
  };
  /*
   * Opens beside the tab, clear of it, so the tab stays clickable as the
   * close control — the thing you clicked to open it is the thing you click
   * to shut it, rather than the control moving somewhere else.
   */
  const panel: React.CSSProperties = {
    position: 'fixed',
    right: 46,
    bottom: 24,
    zIndex: 1200,
    width: 340,
    maxWidth: 'calc(100vw - 64px)',
    height: 460,
    maxHeight: 'calc(100vh - 120px)',
    background: 'var(--white)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    boxShadow: '0 18px 50px rgba(0,0,0,.25)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
  const head: React.CSSProperties = {
    padding: 10,
    background: 'var(--navy)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  };
  const headBtn: React.CSSProperties = {
    border: 'none',
    background: 'rgba(255,255,255,.12)',
    color: 'white',
    borderRadius: 6,
    height: 25,
    padding: '0 9px',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  };
  const bodyS: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: 10 };
  const rowBtn: React.CSSProperties = {
    width: '100%',
    textAlign: 'left',
    border: '1px solid var(--border)',
    background: 'var(--white)',
    borderRadius: 10,
    padding: '7px 10px',
    marginBottom: 10,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12.5,
    color: 'var(--text)',
  };

  return (
    <>
      {askTextDialog}
      <button
        type="button"
        style={fab}
        onClick={() => {
          setOpen((o) => !o);
          setView('list');
          setError(null);
          askNotifyPermission();
        }}
        title="Chat"
        aria-label="Chat"
        aria-expanded={open}
        data-testid="chat-fab"
      >
        {chatIcon(18)}
        <span style={fabLabel}>Chat</span>
        {totalUnread > 0 && (
          <span style={badge} data-testid="chat-unread-badge">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div style={panel} data-testid="chat-panel">
          <div style={head}>
            {view !== 'list' ? (
              <button type="button" style={headBtn} onClick={() => setView('list')} data-testid="chat-back">
                ←
              </button>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {chatIcon(15)}
              </span>
            )}
            <div style={{ fontWeight: 800, fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {view === 'thread' && activeConv
                ? conversationLabel(activeConv, myId)
                : view === 'new'
                  ? 'New chat'
                  : 'Chat'}
            </div>
            {view === 'list' && (
              <button type="button" style={headBtn} onClick={() => setView('new')} data-testid="chat-new">
                + New
              </button>
            )}
            {/*
              DIRECTOR INVESTIGATION EXPORT (wired 2026-08-16). The endpoint
              has existed since CHAT-001 and nothing offered it — the header
              of this very file names it as "the only other read path".
              IMPORT-EXPORT-AUDIT-001 FAULT 3.

              Directors only, and it asks for a written reason the server
              insists on (10 characters minimum) and audits BEFORE returning
              anything. Deliberately not a quiet icon: an audited read of
              somebody's messages should look like what it is.
            */}
            {view === 'thread' && activeConv && isDirector && (
              <button
                type="button"
                style={headBtn}
                onClick={() => { void runInvestigationExport(); }}
                title="Export this conversation for an investigation — recorded against your name"
                data-testid="chat-investigation-export"
              >
                {exporting ? '…' : 'Export'}
              </button>
            )}
            <button type="button" style={headBtn} onClick={() => setOpen(false)} data-testid="chat-close">
              ✕
            </button>
          </div>

          {error && (
            <div style={{ padding: '7px 10px', fontSize: 11.5, color: 'var(--red)' }}>
              {error}
            </div>
          )}

          {/* ── Conversation list ── */}
          {view === 'list' && (
            <div style={bodyS}>
              {convsQ.isLoading && (
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Loading…</div>
              )}
              {!convsQ.isLoading && conversations.length === 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
                  No conversations yet. Hit <strong>+ New</strong> to message a colleague —
                  one person or a group.
                </div>
              )}
              {conversations.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  style={rowBtn}
                  onClick={() => openThread(c)}
                  data-testid={`chat-conv-${c.id}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <strong style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {conversationLabel(c, myId)}
                    </strong>
                    {c.unreadCount > 0 && (
                      <span style={{ ...badge, position: 'static' }}>{c.unreadCount}</span>
                    )}
                  </div>
                  {c.lastMessage && (
                    <div style={{ fontSize: 11, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.lastMessage.senderName}: {c.lastMessage.body} · {timeShort(c.lastMessage.createdAt, timeZone)}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* ── New chat ── */}
          {view === 'new' && (
            <div style={bodyS}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>
                Pick one person for a direct chat, or several for a group.
              </div>
              {(directoryQ.data ?? []).map((p) => {
                const on = picked.has(p.userId);
                return (
                  <button
                    key={p.userId}
                    type="button"
                    style={{ ...rowBtn, borderColor: on ? 'var(--navy)' : 'var(--border)', background: on ? 'rgba(29,106,245,.08)' : 'var(--white)' }}
                    onClick={() => {
                      setPicked((prev) => {
                        const next = new Set(prev);
                        if (next.has(p.userId)) next.delete(p.userId);
                        else next.add(p.userId);
                        return next;
                      });
                    }}
                    data-testid={`chat-pick-${p.userId}`}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: isOnline(p.lastSeen) ? 'var(--green)' : 'var(--border)',
                        marginRight: 7,
                      }}
                    />
                    {p.name}
                    {on ? '  ✓' : ''}
                  </button>
                );
              })}
              {picked.size > 1 && (
                <input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Group name (optional)"
                  maxLength={120}
                  style={{ width: '100%', boxSizing: 'border-box', height: 25, padding: '0 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--white)', fontSize: 12.5, margin: '0 0 10px' }}
                  data-testid="chat-group-name"
                />
              )}
              <button
                type="button"
                disabled={picked.size === 0 || startMut.isPending}
                onClick={() => startMut.mutate()}
                className="btn sm pri"
                style={{ width: '100%', justifyContent: 'center' }}
                data-testid="chat-start"
              >
                {startMut.isPending
                  ? 'Starting…'
                  : picked.size > 1
                    ? `Start group (${picked.size})`
                    : 'Start chat'}
              </button>
            </div>
          )}

          {/* ── Thread ── */}
          {view === 'thread' && activeConv && (
            <>
              <div style={bodyS} data-testid="chat-thread">
                {messages.map((m) => {
                  const mine = m.senderUserId === myId;
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                      <div
                        style={{
                          maxWidth: '80%',
                          background: mine ? 'var(--navy)' : 'rgba(13,35,64,.06)',
                          color: mine ? 'white' : 'var(--text)',
                          borderRadius: 10,
                          padding: '7px 10px',
                          fontSize: 12.5,
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {!mine && (
                          <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.7, marginBottom: 2 }}>
                            {m.senderName}
                          </div>
                        )}
                        {m.body}
                        <div style={{ fontSize: 9.5, opacity: 0.6, textAlign: 'right', marginTop: 2 }}>
                          {timeShort(m.createdAt, timeZone)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={threadEndRef} />
              </div>
              <div style={{ display: 'flex', gap: 10, padding: 10, borderTop: '1px solid var(--border)' }}>
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && draft.trim() && !sendMut.isPending) {
                      sendMut.mutate({ conversationId: activeConv.id, body: draft.trim() });
                    }
                  }}
                  placeholder="Type a message…"
                  maxLength={4000}
                  style={{ flex: 1, height: 25, padding: '0 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--white)', fontSize: 12.5 }}
                  data-testid="chat-input"
                />
                <button
                  type="button"
                  disabled={!draft.trim() || sendMut.isPending}
                  onClick={() => sendMut.mutate({ conversationId: activeConv.id, body: draft.trim() })}
                  className="btn sm pri"
                  data-testid="chat-send"
                >
                  Send
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
